import { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import { getTPOContact } from "../../services/userApi";
import { error as logAppInsightError } from '../../utils/logger';
import { logError } from "../../utils/logError";
import { getAccessToken, getServiceAccessToken } from "../../services/authApi";
import { fieldReader, getMilestoneFreeRoles, getDisclosureTrackingLogs } from "../../services/loanApi";
import { FaLock, FaLockOpen } from "react-icons/fa";
import './summary.css'
import { format } from 'date-fns';
import { formatCurrency, formatRate } from '../../utils/format'
import LoanSummaryFacebookLoader from './loanSummaryFacebookLoader';
import { Download } from "lucide-react";
import {
  combinedFields,
  getMostRecentExceptionDateFields
} from './loanSummaryFields';
import {
  fetchLatestApprovalCertificate as fetchApprovalCertificateFromApi,
  downloadApprovalCertificate as downloadApprovalCertificateFromApi,
  getComplianceReportAttachmentId,
  getComplianceReportDownloadUrl,
  downloadComplianceReportPdf
} from '../../services/documentApi';
import { FiAlertTriangle } from "react-icons/fi";
import { LOAN_PROGRAM_CODES } from '../../config/organizations';
import { isLoanProgram } from '../../utils/loanProgramUtil';
import CancelLoan from './cancelLoan';

const Index = ({ isPageCancelLoan = false }) => {
  const [milestones, setMilestones] = useState([]);
  const [loanDetails, setLoanDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loanID, setLoanID] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [loanPurpose, setLoanPurpose] = useState('');
  const [approvalAttachmentIds, setApprovalAttachmentIds] = useState({
    conditional: null,
    final: null
  });
  const [tooltip, setTooltip] = useState({ text: "", x: 0, y: 0, visible: false });
  const [lockValidationStatus, setLockValidationStatus] = useState('');
  const [loanType, setLoanType] = useState('');
  const [contacts, setContacts] = useState([]);
  const [complianceReportAttachmentId, setComplianceReportAttachmentId] = useState(null);
  const [loanDocType, setLoanDocType] = useState('');

  const [loanCurrentFolder, setLoanCurrentFolder] = useState('');
  const [isCorrespondent, setIsCorrespondentLoan] = useState('');

  useEffect(() => {
    if (window.elli && window.elli.script && window.elli.script.guest) {
      window.elli.script.guest.create();
      initializeAuthObject();
    } else {
      console.warn("elli.script.guest is not available.");
    }
  }, []);

  const initializeAuthObject = async () => {
    try {
      window.elli.script.getObject("loan").then(async function (loanObject) {
        loanObject.all().then(async function (loan) {

          const loanObject = loan?.loan ?? loan;

          const tpoAuthObject = await window.elli.script.getObject("auth");
          let user = null;
          let allContacts = [];

          // AE from Milestone Free Roles (Account Executive)
          try {
            const authCode = await tpoAuthObject.createAuthCode();
            const token = await getAccessToken(authCode);

            const roles = await getMilestoneFreeRoles(token, loanObject.id);

            const aeItem = Array.isArray(roles)
              ? roles.find(r => {
                const roleName = String(r?.loanAssociate?.role?.entityName || "").trim().toLowerCase();
                return roleName === "account executive";
              })
              : null;

            const aeAssoc = aeItem?.loanAssociate;

            if (aeAssoc) {
              allContacts.push({
                role: aeAssoc?.role?.entityName || "Account Executive",
                userName: aeAssoc?.user?.entityName || "",
                email: aeAssoc?.email || "",
                phone: aeAssoc?.phone || ""
              });
            }
          } catch (err) {
            console.error("Error fetching AE from milestoneFreeRoles:", err);
          }

          try {

            if (loanObject?.channel === "Correspondent") {
              const correspondentRoleOrder = [
                "Account Executive",
                "Sales Executive",
                "Support",
              ];

              const authCodes = await tpoAuthObject.createAuthCode();
              const response = await getTPOContact(authCodes, user.id, "");

              if (response) {

                let aeList = [];

                if (response.companies?.length > 0) {
                  const company = response.companies[0];

                  // Prefer company AE, fallback to parent AE
                  if (company.salesRepAe?.length > 0) {
                    aeList = company.salesRepAe;
                  } else if (response.parent?.salesRepAe?.length > 0) {
                    aeList = response.parent.salesRepAe;
                  }
                }

                // Normalize Sales Rep AE → Contact format
                const salesExecContacts = aeList.map(ae => ({
                  role: "Sales Executive",
                  userName: ae.userName || ae.name || "",
                  email: ae.email || "",
                  phone: ae.phone || "",
                }));

                // AE already exists in allContacts (added earlier)
                allContacts = [...allContacts, ...salesExecContacts];

                // Static correspondent sorting
                const sortedContacts = allContacts.sort(
                  (a, b) =>
                    correspondentRoleOrder.indexOf(a.role) -
                    correspondentRoleOrder.indexOf(b.role)
                );

                setContacts(sortedContacts);
              }
            }
            else {
              // Process milestones
              if (loanObject?.milestones) {
                const rolesToCheck = ["Account Manager", "Underwriter", "Closer"];

                // keep only first occurrence of each role
                const roleSeen = new Set();

                const milestoneContacts = loanObject.milestones
                  .map((milestone) => {
                    const assoc = milestone?.loanAssociate;
                    if (
                      assoc &&
                      assoc.user &&
                      assoc.role &&
                      rolesToCheck.includes(assoc.role.entityName) &&
                      !roleSeen.has(assoc.role.entityName) // only first time we see this role
                    ) {
                      roleSeen.add(assoc.role.entityName);
                      return {
                        milestoneName: milestone.name,
                        role: assoc.role.entityName,
                        userName: assoc.user.entityName,
                        email: assoc.email || "",
                        phone: assoc.phone || "",
                      };
                    }
                    return null;
                  })
                  .filter((item) => item !== null);

                allContacts = [...allContacts, ...milestoneContacts];
              }

              // Sort contacts by fixed order
              const roleOrder = [
                "Account Executive",
                "Account Manager",
                "Underwriter",
                "Closer",
              ];
              const sortedContacts = allContacts.sort(
                (a, b) => roleOrder.indexOf(a.role) - roleOrder.indexOf(b.role)
              );
              setContacts(sortedContacts);
            }
          }
          catch (err) {
          }

          setLoanID(loanObject.id);
        });
      });

    } catch (error) {

      const errorCode = logError("LOAN_SUMMARY_ERROR");
      logAppInsightError("Loan Summary Initialization Failed", {
        component: "LoanSummary",
        problemId: errorCode,
        errorCode,
        rawError: error,
        errorMessage: `${errorCode} - Failed to load Loan Summary Information.`
      });
    }
  };

  useEffect(() => {
    if (loanID) {
      const loadData = async () => {
        try {
          const tpoAuthObject = await window.elli.script.getObject("auth");
          const authCode = await tpoAuthObject.createAuthCode();
          const token = await getAccessToken(authCode);

          if (!token) {
            throw new Error("Token not retrieved");
          }
          setAccessToken(token);

          const fieldData = await fieldReader(token, combinedFields, loanID);

          setLoanCurrentFolder(fieldData["LOANFOLDER"] || '');
          setLoanPurpose(fieldData["19"] || '');
          setLockValidationStatus(fieldData["4788"] || "");
          setLoanType(fieldData["CX.LOAN.TYPE"]);

          const milestonesFetcher = getMilestonesFetcher(fieldData);

          const [milestonesData, loanDetailsData] = await Promise.all([
            milestonesFetcher(fieldData),
            fetchLoanDetails(loanID, fieldData)
          ]);

          const visibleLoanDetails = loanDetailsData.filter(card => card.show);

          setMilestones(milestonesData);
          setLoanDetails(visibleLoanDetails);

        } catch (error) {
          const errorCode = logError("LOAN_SUMMARY_LOAD_ERROR");
          logAppInsightError("Loan Summary Load Failure", {
            component: "LoanSummary",
            problemId: errorCode,
            errorCode,
            rawError: error,
            errorMessage: `${errorCode} - Loan summary data failed to load.`
          });
        } finally {
          setLoading(false);
        }
      };
      loadData();
    }
  }, [loanID]);


  // Mock API call for milestones
  const fetchMilestones = async (fields) => {
    try {

      const milestoneMap = [
        { id: 1, title: 'Loan Created', field: "Log.MS.Date.Started" },
        { id: 2, title: 'Registered', field: "1401" },
        { id: 3, title: 'Setup', field: "Log.MS.Date.Setup" },
        { id: 4, title: 'Submitted to UW', field: "Log.MS.Date.Submitted to UW" },
        { id: 5, title: 'Conditional Approval', field: "Log.MS.Date.Initial Approval" },
        { id: 6, title: 'In for Condition Review', field: "Log.MS.Date.Submit for Condition Review" },
        { id: 7, title: 'Submitted for Final Review', field: "Log.MS.Date.Submit for Final Review" },
        { id: 8, title: 'Clear to Close', field: "Log.MS.Date.CTC" },
        // { id: 9, title: 'Ready for Docs', field: "Log.MS.Date.Ready for Docs" },
        { id: 9, title: 'Funded', field: "Log.MS.Date.Funding" },
      ];

      let foundFirstIncomplete = false;

      // Enforce milestone completion one after other
      return milestoneMap.map(({ id, title, field }) => {
        const fieldVal = fields[field];
        let isCompleted = fieldVal && fieldVal !== "//";
        if (field == "Log.MS.Date.Submit for Condition Review" && isCompleted) {
          if (fields["Log.MS.Date.Conditional Approval"] !== "//" && fields["Log.MS.Date.Submit for Final Review"] === "//") {
            isCompleted = false;
          }
        }

        const completed = !foundFirstIncomplete && isCompleted;

        if (!isCompleted) foundFirstIncomplete = true;

        return { id, title, completed };
      });

    } catch (error) {
      const errorCode = logError("LOAN_MILESTONE_ERROR");
      logAppInsightError("Error fetching loan milestones", {
        component: "LoanSummary",
        problemId: errorCode,
        errorCode,
        rawError: error,
        errorMessage: `${errorCode} - Failed to fetch milestone data.`
      });
      return [];
    }
  };

  const fetchMilestonesNonDelegate = async (fields) => {
    try {

      // const milestoneMap = [
      //   { id: 1, title: 'File Started', field: "Log.MS.Date.Started" },
      //   { id: 2, title: 'Registered', field: "Log.MS.Date.Registered" }, //1401
      //   { id: 3, title: 'Submitted to UW', field: "Log.MS.Date.Submitted to UW" },
      //   { id: 4, title: 'Initial Approval', field: "Log.MS.Date.Initial Approval" },
      //   { id: 5, title: 'Conditional Approval', field: "Log.MS.Date.Conditional Approval" },
      //   { id: 6, title: 'Submit for Final Review', field: "Log.MS.Date.Submit for Final Review" },
      //   { id: 7, title: 'Clear to Close', field: "Log.MS.Date.CTC" },
      //   { id: 8, title: 'Closed Loan Submitted', field: "Log.MS.Date.Closed Loan Submitted" },
      //   { id: 9, title: 'In Purchase Review', field: "Log.MS.Date.In Purchase Review" },
      //   { id: 10, title: 'Purchasing Suspense', field: "Log.MS.Date.Purchasing Suspense" },
      //   { id: 11, title: 'Purchasing Condition Review', field: "Log.MS.Date.Purchasing Condition Review" },
      //   { id: 12, title: 'Cleared For Purchase', field: "Log.MS.Date.Cleared for Purchase" },
      //   { id: 13, title: 'Purchased', field: "Log.MS.Date.Purchase" }
      // ];

      const milestoneMap = [
        { id: 1, title: 'File Started', field: "Log.MS.Date.Started" },
        { id: 2, title: 'Registered', field: "1401" }, //1401
        { id: 3, title: 'Submitted to UW', field: "Log.MS.Date.Submitted to UW" },
        { id: 4, title: 'In UW Review', field: "Log.MS.Date.In UW Review" },
        { id: 5, title: 'UW Decision', field: "Log.MS.Date.UW Decision" },
        { id: 6, title: 'Condition Review', field: "Log.MS.Date.Condition Review" },
        { id: 7, title: 'Clear to Close', field: "Log.MS.Date.CTC" },
        { id: 8, title: 'Closed Loan Submitted', field: "Log.MS.Date.Closed Loan Submitted" },
        { id: 9, title: 'In Purchase Review', field: "Log.MS.Date.In Purchase Review" },
        { id: 10, title: 'Purchasing Suspense', field: "Log.MS.Date.Purchasing Suspense" },
        { id: 11, title: 'Purchasing Condition Review', field: "Log.MS.Date.Purchasing Condition Review" },
        { id: 12, title: 'Cleared For Purchase', field: "Log.MS.Date.Cleared for Purchase" },
        { id: 13, title: 'Purchased', field: "Log.MS.Date.Purchased" }
      ];

      let foundFirstIncomplete = false;

      // Enforce milestone completion one after other
      return milestoneMap.map(({ id, title, field }) => {
        const fieldVal = fields[field];
        let isCompleted = fieldVal && fieldVal !== "//";
        if (field == "Log.MS.Date.Submit for Condition Review" && isCompleted) {
          if (fields["Log.MS.Date.Conditional Approval"] !== "//" && fields["Log.MS.Date.Submit for Final Review"] === "//") {
            isCompleted = false;
          }
        }

        const completed = !foundFirstIncomplete && isCompleted;

        if (!isCompleted) foundFirstIncomplete = true;

        return { id, title, completed };
      });

    } catch (error) {
      const errorCode = logError("LOAN_MILESTONE_ERROR");
      logAppInsightError("Error fetching loan milestones", {
        component: "LoanSummary",
        problemId: errorCode,
        errorCode,
        rawError: error,
        errorMessage: `${errorCode} - Failed to fetch milestone data.`
      });
      return [];
    }
  };

  const fetchMilestonesDelegate = async (fields) => {
    try {

      const milestoneMap = [
        { id: 1, title: 'Loan Created', field: "Log.MS.Date.Started" },
        { id: 2, title: 'Registered', field: "1401" }, //1401
        { id: 3, title: 'Closed Loan Submitted', field: "Log.MS.Date.Closed Loan Submitted" },
        { id: 4, title: 'In Purchase Review', field: "Log.MS.Date.In Purchase Review" },
        { id: 5, title: 'Purchasing Suspense', field: "Log.MS.Date.Purchasing Suspense" },
        { id: 6, title: 'Purchasing Condition Review', field: "Log.MS.Date.Purchasing Condition Review" },
        { id: 7, title: 'Cleared For Purchase', field: "Log.MS.Date.Cleared for Purchase" },
        { id: 8, title: 'Purchased', field: "Log.MS.Date.Purchased" }
      ];

      let foundFirstIncomplete = false;

      // Enforce milestone completion one after other
      return milestoneMap.map(({ id, title, field }) => {
        const fieldVal = fields[field];
        let isCompleted = fieldVal && fieldVal !== "//";
        if (field == "Log.MS.Date.Submit for Condition Review" && isCompleted) {
          if (fields["Log.MS.Date.Conditional Approval"] !== "//" && fields["Log.MS.Date.Submit for Final Review"] === "//") {
            isCompleted = false;
          }
        }

        const completed = !foundFirstIncomplete && isCompleted;

        if (!isCompleted) foundFirstIncomplete = true;

        return { id, title, completed };
      });

    } catch (error) {
      const errorCode = logError("LOAN_MILESTONE_ERROR");
      logAppInsightError("Error fetching loan milestones", {
        component: "LoanSummary",
        problemId: errorCode,
        errorCode,
        rawError: error,
        errorMessage: `${errorCode} - Failed to fetch milestone data.`
      });
      return [];
    }
  };

  const formatDate = (val) => {
    if (!val || val === "//") return "-";
    return val.split("T")[0]
      .split("-")
      .slice(1)
      .concat(val.split("T")[0].split("-")[0])
      .join("/");
  };

  const isBusinessLoan = (loanTypeValue) =>
    loanTypeValue?.toLowerCase().includes("business");

  const shouldHideLEAndCD = (loanOccupancy, loanTypeValue) => {
    const occupancy = loanOccupancy?.trim();
    if (occupancy === 'Investor') return true;
    if (!occupancy && isBusinessLoan(loanTypeValue)) return true;
    return false;
  };


  const getMilestonesFetcher = (loanFields) => {
    if (isCorrespondentLoan(loanFields["2626"])) {
      return isDelegateLoan(loanFields["TPO.X88"])
        ? fetchMilestonesDelegate
        : fetchMilestonesNonDelegate;
    }

    return fetchMilestones; // existing flow
  };

  const isCorrespondentLoan = (loanFields) => {
    return loanFields === "Correspondent";
  };

  const isDelegateLoan = (loanFields) => {
    return loanFields === "Y";
  };

  const getLESignedFromDisclosureLogs = async (token, loanId) => {
    try {

      const logs = await getDisclosureTrackingLogs(token, loanId);
      if (!logs || logs.length === 0) return "";

      // Filter only LE + eDisclosure
      const leLogs = logs.filter(log =>
        log.disclosedMethod === "eDisclosure" &&
        Array.isArray(log.contents) &&
        log.contents.includes("LE")
      );

      if (leLogs.length === 0) return "";

      let eSignedDates = [];

      leLogs.forEach(log => {
        (log.disclosureRecipients || [])
          .filter(recipient => recipient.recipientType === "Borrower")
          .forEach(recipient => {
            const signedDate = recipient?.tracking?.eSignedDate;
            if (signedDate) {
              eSignedDates.push(signedDate);
            }
          });
      });

      if (eSignedDates.length === 0) return "";

      // Sort ascending → oldest first
      eSignedDates.sort((a, b) => new Date(a) - new Date(b));

      const oldestUtcDate = eSignedDates[0];

      // Convert UTC → PST
      const pstDate = new Date(oldestUtcDate).toLocaleString("en-US", {
        timeZone: "America/Los_Angeles"
      });

      const formattedDate = format(new Date(pstDate), "MM/dd/yyyy");

      return `${formattedDate} (PST)`;

    } catch (error) {
      console.error("Error getting LE Signed date:", error);
      return "";
    }
  };



  // Add helper to hide Entity Review Date if not business loan
  const shouldShowEntityReview = (loanTypeValue) => isBusinessLoan(loanTypeValue);

  const fetchLoanDetails = async (loanid, fields) => {
    try {

      const tpoAuthObject = await window.elli.script.getObject("auth");
      const authCode = await tpoAuthObject.createAuthCode();
      const token = await getServiceAccessToken(authCode);

      const leSignedDate = await getLESignedFromDisclosureLogs(token, loanid);

      const parseNumber = (val) => parseFloat((val || "0").replace(/,/g, '')).toFixed(2);

      const getLatestCuReviewActionDate = (historyValue, predicate) => {
        try {
          const history = Array.isArray(historyValue)
            ? historyValue
            : JSON.parse(historyValue || "[]");

          const matched = history
            .filter(item => item && predicate(item))
            .sort((a, b) => {
              const aDate = new Date(a.createdAt || 0).getTime();
              const bDate = new Date(b.createdAt || 0).getTime();
              return bDate - aDate;
            });

          return matched[0]?.actionDate || "";
        } catch (e) {
          return "";
        }
      };

      const cuReviewHistory = fields["CX.CUREVIEW.HISTORY"];
      const cuReviewRequestedDate = getLatestCuReviewActionDate(
        cuReviewHistory,
        item => item.cuType.toLowerCase() === "cu1" && item.actionType.toLowerCase() === "requested"
      );
      const cuReviewReviewedDate = getLatestCuReviewActionDate(
        cuReviewHistory,
        item => item.cuType.toLowerCase() === "cu1" && item.actionType.toLowerCase() !== "requested"
      );

      const mostRecentException = getMostRecentExceptionDateFields(fields);

      const keyDates = [
        { name: 'Application Date', field: "Log.MS.Date.Started" },
        { name: 'LE Sent', field: "3152" },
        { name: "LE Signed", consumerOnly: true },
        { name: 'eConsent', field: "3983" },
        { name: 'Setup', field: "Log.MS.Date.Setup" },
        { name: 'Submitted to UW', field: "Log.MS.Date.Submitted to UW" },
        { name: 'Estimated Closing ', field: "2966" },
        { name: 'Conditional Approval', field: "Log.MS.Date.Initial Approval" },
        { name: 'Appraisal Received Date', field: "CX.APPR.FULFILLEDDATE" },
        { name: 'Condition Review', field: "Log.MS.Date.Submit for Condition Review" },
        //{ name: 'Final UW', field: "Log.MS.Date.Final UW" }, //removed after new milestone changes
        { name: 'CD Sent', field: "3977" },
        { name: "CD Signed", field: "3978", consumerOnly: true },
        { name: 'Clear to Close', field: "Log.MS.Date.CTC" },
        // { name: 'Ready for Docs', field: "Log.MS.Date.Ready for Docs" },
        { name: 'Docs Out', field: "Log.MS.Date.Docs Out" },
        { name: 'Funded', field: "Log.MS.Date.Funding" },
        { name: 'Lock Date', field: "761" },
        { name: 'Lock Expiration Date', field: "762" },
        { name: 'Approval Expiration Date', field: "2302" },
        { name: 'Close of Escrow', field: "CX.COE.DATE" },
        { name: 'Entity Requested Date', field: "CX.ER.REQ.DATE" },
        { name: 'Entity Reviewed Date', field: "CX.ER.REV.DATE" },
        { name: 'CDA Ordered Date', field: "2359" },
        { name: 'CDA Received Date', field: "2360" },


        // CU Review Dates (Review Date only for now)
        {
          name: "CU Review - Requested Date",
          field: "CX.CU1.REQUEST01.REQ.DATE"
        },
        {
          name: "CU Review - Reviewed Date",
          field: "CX.CU1.REQUEST01.REV.DATE"
        },

        // Most recent exception (Pre-Submission or Post-Submission 1-3).
        // The corresponding Secondary Decision Date is the final decision date.
        { name: "Exception - Request Date", field: mostRecentException?.requestField },
        { name: "Exception - Decision Date", field: mostRecentException?.decisionField },

        // Condo Review (all products)
        { name: "Condo Review - Request Date", field: "CX.CONDR.PRO.REV.REQ.DATE" },
        { name: "Condo Review - Decision Date", field: "CX.CONDR.DEC.DATE" },

        // TODO
        // { name: "Exception - Request Date", field: "CX.EXCEPTION.REQUEST.DATE" },
        // { name: "Exception - Review Date", field: "CX.EXCEPTION.REVIEW.DATE" },
        // { name: "4506 Ordered Date", field: "..." }
      ];

      const shouldHide = shouldHideLEAndCD(fields["1811"], fields["CX.LOAN.TYPE"]);
      const isCorres = isCorrespondentLoan(fields["2626"]);
      setIsCorrespondentLoan(isCorres);

      const business = isBusinessLoan(fields["CX.LOAN.TYPE"]);

      const dates = keyDates
        .filter(d => {
          if (shouldHide && (d.name === "LE Sent" || d.name === "CD Sent")) return false;

          // Consumer-only rows: hide for Business
          if (d.consumerOnly && business) return false;

          if (d.name === "Entity Review Date" && business === false) {
            // keep your existing logic, or use shouldShowEntityReview()
          }

          if (d.name === "Entity Review Date" && !shouldShowEntityReview(fields["CX.LOAN.TYPE"])) return false;

          return true;
        })
        .map(d => ({
          name: d.name,
          correspondingDate:
            d.name === "LE Signed"
              ? (leSignedDate || "-")
              : d.name === "CU Review - Requested Date"
                ? (cuReviewRequestedDate ? formatDate(cuReviewRequestedDate) : "-")
                : d.name === "CU Review - Reviewed Date"
                  ? (cuReviewReviewedDate ? formatDate(cuReviewReviewedDate) : "-")
                  : (d.field ? formatDate(fields[d.field]) : "-")
        }));


      const loanProgram = fields["1401"];
      const productName = fields["CX.LOAN.PRODUCT.NAME"];
      const topDTI = fields["740"] || "0";
      const bottomDTI = fields["742"] || "0";
      // Products that show a real DTI. Canonical codes, so this reads the
      // same whichever company's Encompass the loan came from - both ALLY

      const targetPrograms = [
        LOAN_PROGRAM_CODES.PROGRAM_001, // ALLY
        LOAN_PROGRAM_CODES.PROGRAM_004, // ACTIVATOR / SUNNY
        LOAN_PROGRAM_CODES.PROGRAM_006, // ACTIVATOR ITIN / SUNNY ITIN
        LOAN_PROGRAM_CODES.PROGRAM_007, // AMBASSADOR / PASSPORT
        LOAN_PROGRAM_CODES.PROGRAM_008, // SUPER JUMBO / STARRY
        LOAN_PROGRAM_CODES.PROGRAM_002, // ACCELERATOR / BREEZY
        LOAN_PROGRAM_CODES.PROGRAM_005, // ACCELERATOR ITIN / BREEZY ITIN
        LOAN_PROGRAM_CODES.PROGRAM_003, // ACCELERATOR INCOME / BREEZY INCOME
        LOAN_PROGRAM_CODES.PROGRAM_009, // ACTIVATOR PRIME / SUNNY SELECT
        LOAN_PROGRAM_CODES.PROGRAM_010,  // ACCELERATOR EQUITY FIRST / CLOUDY
        LOAN_PROGRAM_CODES.PROGRAM_011,
        LOAN_PROGRAM_CODES.PROGRAM_012,
        LOAN_PROGRAM_CODES.PROGRAM_013   // ACTIVATOR EQUITY FIRST (ChampsTPO only)
      ];

      const isTargetValue = isLoanProgram(productName, targetPrograms);

      const dtiCalculation = isTargetValue && loanProgram !== ""
        ? `${topDTI}% / ${bottomDTI}%`
        : "N/A";

      setLoanDocType(fields["CX.DOC.TYPE"], "");

      const rawValue = parseNumber(fields["CX.NONQ.30"]);

      const incomeValue =
        rawValue == null || rawValue === 0 || rawValue === "0.00"
          ? parseNumber(fields["CX.GROSS.RENT"])
          : rawValue;

      const qualifyingIncome = formatCurrency(parseNumber(incomeValue), "en-US", "USD");
      const piti = formatCurrency(parseNumber(fields["912"]), "en-US", "USD");
      const hoaDue = formatCurrency(parseNumber(fields["233"]), "en-US", "USD");
      const insurance = formatCurrency(parseNumber(fields["230"]), "en-US", "USD");
      const propertyTaxes = formatCurrency(parseNumber(fields["1405"]), "en-US", "USD");

      return [
        {
          id: 1,
          type: 'loan-amount',
          show: true,
          data: {
            amount: formatCurrency(parseNumber(fields["2"]), "en-US", "USD"),
            baseLoanAmount: formatCurrency(parseNumber(fields["1109"]), "en-US", "USD"),
            purposeOfRefi: fields["299"] || "N/A"
          }
        },
        {
          id: 2,
          type: 'interest-rate',
          show: true,
          data: {
            rate: formatRate(fields["3"]),
            status: (fields["CX.LOAN.LOCK.STATUS"] || "").toLowerCase().includes("initiallockrequestsubmitted") &&
              !(fields["4209"] || "").toLowerCase().includes("active lock")
              ? "Lock Requested"
              : (fields["4209"] || "").toLowerCase().includes("active lock")
                ? "Locked"
                : "Unlocked",
            amortizationType: fields["608"] || "N/A",
            amortizationTerm: fields["4"] || "0",
            loanProgram: fields["1401"] || "N/A",
            isExpired: (() => {
              try {
                const val = fields["762"];
                if (!val) return false;
                const parts = String(val).split('/');
                if (parts.length !== 3) return false;
                const [mm, dd, yyyy] = parts.map(p => parseInt(p, 10));
                // expiration moment = end of that day in Pacific (23:59:59)
                const expDate = new Date(yyyy, mm - 1, dd, 23, 59, 59);

                const pacificNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));

                return expDate < pacificNow;
              } catch (e) {
                return false;
              }
            })()
          }
        },
        {
          id: 3,
          type: 'property',
          show: true,
          data: {
            address: fields["11"],
            city: fields["12"],
            state: fields["14"],
            zip: fields["15"],
            propertyType: fields["1811"] || "",
            units: fields["16"] ? `${fields["16"]} unit${parseInt(fields["16"]) > 1 ? 's' : ''}` : 'N/A',
            estimatedValue: formatCurrency(parseNumber(fields["1821"]), "en-US", "USD"),
            appraisedValue: formatCurrency(parseNumber(fields["356"]), "en-US", "USD"),
            purchasePrice: formatCurrency(parseNumber(fields["136"]), "en-US", "USD"),
            floodIndicator: fields["CX.FEES.FLOOD.INDICATOR"] || "N/A"
          }
        },
        {
          id: 4,
          type: 'loan-status',
          show: true,
          data: {
            status: fields["MS.STATUS"] || "N/A",
            date: fields["MS.STATUSDATE"]
              ? new Date(fields["MS.STATUSDATE"]).toISOString().split('T')[0]
              : "-",
            pni: formatCurrency(parseNumber(fields["228"]), "en-US", "USD"),
            fico: fields["VASUMM.X23"] || "-",
            // reserves: formatCurrency(parseNumber(fields["1548"]), "en-US", "USD"),
            reserves: fields["1548"] ? fields["1548"] + " months" : " N/A",
            dti: dtiCalculation,
            ltv: fields["353"] || "0",
            dscr: fields["CX.DSCR.CALCULATED"] > 0 ? fields["CX.DSCR.CALCULATED"] : 'N/A',
            ppPenalty: fields["675"],
            pppIsYes: fields["675"]?.toLowerCase() === 'y'.toLowerCase(),
            pppTerm: fields["CX.PPP.TERM"],
            ppeType: fields["CX.PPP.TYPE"],
          }
        },
        {
          id: 5,
          type: 'key-dates',
          show: true,
          data: { dates }
        },
        {
          id: 6,
          type: 'compensation-factors',
          show: true,
          data: {
            compType: fields["LCP.X1"] || "N/A",
            borrowerPaidBrokerFee: fields["389"] || "",
            compTypePer: fields["LCP.X6"] ? `${fields["LCP.X6"]}%` : "0%",
            compTypeAmt: fields["LCP.X7"] ? `$${fields["LCP.X7"]}` : "$0",
            compTypeTotal: fields["LCP.X8"] ? `$${fields["LCP.X8"]}` : "$0",
            lenderCreditPer: fields["NEWHUD.X1143"] ? `${fields["NEWHUD.X1143"]}%` : "0%",
            lenderCreditAmt: fields["NEWHUD.X1144"] ? `$${fields["NEWHUD.X1144"]}` : "$0",
            yspToBrokerPer: fields["CX.BROKER.YSP.PCT"] ? `${fields["CX.BROKER.YSP.PCT"]}%` : "0%",
            yspToBrokerAmt: fields["CX.BROKER.YSP.AMT"] ? `$${fields["CX.BROKER.YSP.AMT"]}` : "$0",
          }
        },
        {
          id: 7,
          type: 'compliance-review',
          show: true,
          data: {
            highCostResult: fields["COMPLIANCEREVIEW.X7"] || "N/A",
            higherPricedResult: fields["COMPLIANCEREVIEW.X16"] || "N/A",
            stateRulesResult: fields["COMPLIANCEREVIEW.X12"] || "N/A",
          }
        },
        {
          id: 8,
          type: 'milestone-contacts',
          show: true,
          data: {
            contacts
          }
        },
        {
          id: 9,
          type: 'bp-income-piti',
          show: true,
          data: {
            // BP only (business loan)
            showQualifyingIncome: isBusinessLoan(fields["CX.LOAN.TYPE"]),
            qualifyingIncome,

            // All products
            piti,
            hoaDue,
            insurance,
            propertyTaxes
          }
        },
      ];

    } catch (error) {
      const errorCode = logError("LOAN_DETAILS_ERROR");
      logAppInsightError("Error fetching loan details", {
        component: "LoanSummary",
        problemId: errorCode,
        errorCode,
        rawError: error,
        errorMessage: `${errorCode} - Failed to fetch loan details.`
      });
      return [];
    }
  };


  const getCurrentMilestoneIndex = () => {
    const firstIncompleteIndex = milestones.findIndex(milestone => !milestone.completed);
    return firstIncompleteIndex === -1 ? milestones.length - 1 : firstIncompleteIndex;
  };

  useEffect(() => {
    const conditionalDone = milestones.find(m => m.title === 'Conditional Approval')?.completed;
    const finalDone = milestones.find(m => m.title === 'Clear to Close')?.completed;

    if (accessToken && loanID && (conditionalDone || finalDone)) {
      fetchLatestApprovalCertificate(accessToken);
    }
  }, [milestones, accessToken, loanID]);

  useEffect(() => {
    if (accessToken && loanID) {
      (async () => {
        try {
          const attachmentId = await getComplianceReportAttachmentId(accessToken, loanID);
          if (attachmentId) {
            setComplianceReportAttachmentId(attachmentId);
          }
        } catch (error) {
          console.error("Error checking Compliance Report attachment:", error);
        }
      })();
    }
  }, [accessToken, loanID]);

  const fetchLatestApprovalCertificate = async () => {
    try {
      const tpoAuthObject = await window.elli.script.getObject("auth");
      const authCode = await tpoAuthObject.createAuthCode();

      const attachmentResult = await fetchApprovalCertificateFromApi(authCode, loanID);
      console.log(attachmentResult);
      if (attachmentResult) {
        setApprovalAttachmentIds({
          conditional: attachmentResult.conditionalAttachmentId || null,
          final: attachmentResult.finalAttachmentId || null
        });
      }
    } catch (err) {
      const errorCode = logError("FETCH_ATTACHMENT_ERROR");
      logAppInsightError("Error fetching Approval Certificate", {
        component: "LoanSummary",
        problemId: errorCode,
        errorCode,
        rawError: err,
        errorMessage: `${errorCode} - Failed to get Approval Certificate.`
      });
    }
  };


  const downloadApprovalCertificate = async (type) => {
    try {
      const tpoAuthObject = await window.elli.script.getObject("auth");
      const authCode = await tpoAuthObject.createAuthCode();
      const attachmentId = type === 'conditional' ? approvalAttachmentIds.conditional : approvalAttachmentIds.final;
      const blob = await downloadApprovalCertificateFromApi(authCode, loanID, attachmentId, type);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = type === 'conditional' ? "Conditional_Approval_Certificate.pdf" : "Final_Approval_Certificate.pdf";
      link.click();
    } catch (err) {
      const errorCode = logError("DOWNLOAD_ATTACHMENT_ERROR");
      logAppInsightError("Download Approval Certificate failed", {
        component: "LoanSummary",
        problemId: errorCode,
        errorCode,
        rawError: err,
        errorMessage: `${errorCode} - Attachment download failed.`
      });
    }
  };


  const renderMilestones = () => {
    const currentIndex = getCurrentMilestoneIndex();

    return (
      <div className="bg-white p-6 mb-6 rounded-lg shadow-sm border border-gray-100">
        <div className="relative">
          {/* Progress line */}
          <div className="absolute left-10 right-10 top-5 h-1 brand-step-track z-0"></div>
          <div className="relative flex justify-between z-10">
            {milestones.map((milestone, index) => (
              <div key={milestone.id} className="flex flex-col items-center relative" style={{ width: `${100 / (milestones.length)}%` }}>
                {/* Milestone circle */}
                <div className={`w-10 h-10 z-10 brand-step-circle
        ${milestone.completed
                    ? 'brand-step-circle--done'
                    : index === currentIndex
                      ? 'brand-step-circle--current'
                      : ''
                  }`}>
                  {milestone.completed ? (
                    <Check size={24} className="text-white" />
                  ) : (
                    <span className="text-sm font-medium">{milestone.id}</span>
                  )}
                </div>

                {/* Milestone label */}
                <div className={`mt-3 text-xs text-center max-w-[100px] ${milestone.completed || index === currentIndex
                  ? 'text-black font-medium'
                  : 'text-gray-500'
                  }`}>
                  {milestone.title === "Conditional Approval"
                    && approvalAttachmentIds.conditional
                    && !approvalAttachmentIds.final
                    && milestones.find(m => m.title === 'Conditional Approval')?.completed ? (
                    <div
                      className="flex items-center gap-1 brand-text cursor-pointer"
                      onClick={() => downloadApprovalCertificate('conditional')}
                      title="Download Conditional Approval Certificate"
                    >
                      <span className="text-sm">{milestone.title}</span>
                      <Download size={18} />
                    </div>
                  ) : milestone.title === "Clear to Close"
                    && approvalAttachmentIds.final
                    && milestone.completed ? (
                    <div
                      className="flex items-center gap-1 brand-text cursor-pointer"
                      onClick={() => downloadApprovalCertificate('final')}
                      title="Download Final Approval Certificate"
                    >
                      <span className="text-sm">{milestone.title}</span>
                      <Download size={18} />
                    </div>
                  ) : (
                    milestone.title
                  )}
                </div>

                {/* Connecting line - improved positioning */}
                {index < milestones.length - 1 && (
                  <div
                    className={`absolute top-5 h-0.5 z-0
            ${milestone.completed ? 'brand-step-line--done' : 'brand-step-line'}`}
                    style={{
                      left: 'calc(50% + 20px)', // Start from right edge of circle
                      width: 'calc(100% - 20px)', // Full width minus circle widths
                    }}
                  ></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderLoanAmountCard = (data) => (
    <div className="bg-white p-6 rounded-lg shadow-sm border">
      <div className="text-3xl font-bold text-gray-900 mb-2">{data.amount}</div>
      <div className="text-sm text-gray-600 mb-4">{data.percentages}</div>
      <div className="grid_summary grid-cols-2 gap-4 text-sm">
        <div>
          <div className="text-gray-500">Base Loan Amount</div>
          <div className="font-medium">{data.baseLoanAmount}</div>
        </div>
        {/* <div>
          <div className="text-gray-500">MI, FF, MIP Financed</div>
          <div className="font-medium">{data.miFinanced}</div>
        </div>
        <div>
          <div className="text-gray-500">Sub. Financing</div>
          <div className="font-medium">{data.subFinancing}</div>
        </div> */}
        {/* <div>
          <div className="text-gray-500">Purpose of ReFi</div>
          <div className="font-medium">{data.purposeOfRefi}</div>
        </div> */}
      </div>
    </div>
  );

  const renderInterestRateCard = (data) => (
    <div className="bg-white p-6 rounded-lg shadow-sm border">
      {/* First Row: Lock icon + Locked % + Exclamation */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          {data.isExpired ? (
            <FaLock className="text-red-600 mr-2" size={20} title="Expired" />
          ) : data.status === "Lock Requested" ? (
            <FaLockOpen className="text-yellow-500 mr-2" size={20} title="Lock Requested" />
          ) : data.status === "Locked" ? (
            <FaLock className="text-orange-600 mr-2" size={20} title="Locked" />
          ) : (
            <FaLockOpen className="text-blue-400 mr-2" size={20} title="Not Locked" />
          )}
          <span className="text-lg font-bold">
            {data.isExpired ? "Expired" : `${data.status} ${data.rate}`}
          </span>
        </div>
      </div>
      <div className="flex items-center justify-between mb-4">
        {/* Exclamation Icon and Text in same line */}
        {lockValidationStatus === "Needs Validation" && (
          <div className="flex items-center gap-2">
            <FiAlertTriangle
              size={20}
              className="text-orange-600 cursor-pointer"
              onMouseEnter={(e) => {
                const tooltipX = e.clientX + 10;
                const tooltipY = e.clientY - 40;
                setTooltip({
                  text: "Re-Price: Click to compare changes",
                  x: tooltipX,
                  y: tooltipY,
                  visible: true,
                });
              }}
              onMouseLeave={() =>
                setTooltip((prev) => ({ ...prev, visible: false }))
              }
              onClick={redirectToLockComparisonPage}
            />
            <div className="text-gray-500 font-bold">
              Loan parameters have changed. Please re-price
            </div>
          </div>
        )}
      </div>

      {/* Loan Info */}
      <div className="grid_summary grid-cols-2 gap-4 text-sm">
        <div>
          <div className="text-gray-500 font-bold">Amortization Type</div>
          <div className="font-medium">{data.amortizationType}</div>
        </div>
        <div>
          <div className="text-gray-500 font-bold">Amortization Term</div>
          <div className="font-medium">{data.amortizationTerm}</div>
        </div>
        <div>
          <div className="text-gray-500 font-bold">Loan Program</div>
          <div className="font-medium">{data.loanProgram}</div>
        </div>
      </div>
    </div>
  );

  const renderPropertyCard = (data) => {
    const {
      address = '',
      city = '',
      state = '',
      zip = '',
      propertyType = '',
      units = '',
      estimatedValue = '$0',
      appraisedValue = '$0',
      purchasePrice = '$0',
      floodIndicator = 'N/A',
    } = data;

    const fullAddress = [address, city, state, zip].filter(Boolean).join(', ');
    const unitLabel = units && !units.toLowerCase().includes('unit') ? `${units} unit${parseInt(units) > 1 ? 's' : ''}` : units;

    return (
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="text-lg font-bold mb-2">{fullAddress || 'N/A'}</div>
        <div className="text-sm text-gray-600 mb-4">{propertyType} {unitLabel}</div>
        <div className="grid_summary grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-gray-500">Estimated Value</div>
            <div className="font-medium">{estimatedValue}</div>
          </div>
          <div>
            <div className="text-gray-500">Appraised Value</div>
            <div className="font-medium">{appraisedValue}</div>
          </div>
          {loanPurpose?.toLowerCase().includes('purchase') && (
            <div>
              <div className="text-gray-500">Purchase Price</div>
              <div className="font-medium">{purchasePrice}</div>
            </div>
          )}
          {!isCorrespondent && (
            <div>
              <div className="text-gray-500">Flood Zone</div>
              <div className="font-medium">{floodIndicator}</div>
            </div>
          )}
        </div>
      </div>
    );
  };


  const renderLoanStatusCard = (data) => (
    <div className="bg-white p-6 rounded-lg shadow-sm border">
      {/* <div className="text-lg font-bold mb-1">{data.status}</div>
      <div className="text-sm text-gray-600 mb-4">{data.date ? format(new Date(data.date), "MM/dd/yyyy") : "-"}</div> */}
      {/* <div className="mb-4">
        <div className="text-sm text-gray-500">Conditions</div>
        <div className="flex items-center">
          <span className="font-medium">{data.conditions}</span>
          <span className="ml-auto text-lg font-bold">{data.conditionsCount}</span>
        </div>
        <button className="text-blue-600 text-sm mt-1">Show Details</button>
      </div> */}
      <div className="text-lg font-bold mb-1">P & I</div>
      <div className="text-sm text-gray-600 mb-4">{data.pni}</div>

      <div className="grid grid-cols-2 gap-4 text-sm">
        {/* <div>
          <div className="text-gray-500">P & I</div>
          <div className="font-medium">{data.pni}</div>
        </div>
        <div></div> */}
        <div>
          <div className="text-gray-500">FICO</div>
          <div className="font-medium">{data.fico}</div>
        </div>
        <div>
          <div className="text-gray-500">Reserves</div>
          <div className="font-medium">{data.reserves}</div>
        </div>

        {loanDocType === "DSCR" ? (
          <div>
            <div className="text-gray-500">DSCR</div>
            <div className="font-medium">{data?.dscr ?? "N/A"}</div>
          </div>
        ) : (
          <div>
            <div className="text-gray-500">DTI</div>
            <div className="font-medium">{data?.dti ?? "N/A"}</div>
          </div>
        )}
        <div>
          <div className="text-gray-500">LTV</div>
          <div className="font-medium">{data?.ltv}%</div>
        </div>

        {data.pppIsYes && (
          <div className='col-span-2'>
            <div className="text-gray-500">Prepayment Penalty</div>
            <div className="font-medium">{data.ppPenalty.toLowerCase() === 'y' ? 'Yes' : 'No'}{(data.pppIsYes && data.pppTerm) && ` - ${data.pppTerm} Months`}{data.ppeType === "Declining" && " (Step Down)"}</div>
          </div>
        )}

      </div>
    </div>
  );

  const renderCreditInfoCard = (data) => (
    <div className="bg-white p-6 rounded-lg shadow-sm border">
      <div className="text-lg font-bold mb-4">Credit Information</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 text-gray-500">Borrower</th>
              <th className="text-center py-2 text-gray-500">Experian</th>
              <th className="text-center py-2 text-gray-500">TransUnion</th>
              <th className="text-center py-2 text-gray-500">Equifax</th>
            </tr>
          </thead>
          <tbody>
            {data.borrowers.map((borrower, index) => (
              <tr key={index}>
                <td className="py-2 font-medium">{borrower.name}</td>
                <td className="text-center py-2">{borrower.experian}</td>
                <td className="text-center py-2">{borrower.transUnion}</td>
                <td className="text-center py-2">{borrower.equifax}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderKeyDatesCard = (data) => (
    <div className="bg-white p-6 rounded-lg shadow-sm border">
      <div className="text-lg font-bold mb-4">Key Dates</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 text-gray-500">Dates</th>
            </tr>
          </thead>
          <tbody>
            {data.dates.map((borrower, index) => (
              <tr key={index}>
                <td className="py-2 font-medium">{borrower.name}</td>
                <td className="text-left py-2">{borrower.correspondingDate}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );


  const renderCompensationTypeCard = (data) => (
    <div className="bg-white p-6 rounded-lg shadow-sm border text-sm">
      <div className="grid grid-cols-[minmax(120px,auto)_1fr] gap-y-2"> {/* Adjusted column widths */}
        <span className="font-bold text-gray-700 whitespace-nowrap">Comp Type:</span> {/* Added whitespace-nowrap to title */}
        <span className="text-gray-900 font-normal">
          {data.compType}
          {data.compType?.toLowerCase() === "borrower paid" && data.borrowerPaidBrokerFee && (
            <> ({data.borrowerPaidBrokerFee}%)</>
          )}
        </span>

        {data.compType && data.compType.toLowerCase().includes("lender") && (
          <>
            <span></span>
            <span className="text-gray-900 font-normal">
              {data.compTypePer} + {data.compTypeAmt} = {data.compTypeTotal}
            </span>
          </>
        )}

        <span className="font-bold text-gray-700 whitespace-nowrap">Lender Credit to Borrower:&nbsp;&nbsp;</span> {/* Shortened label and added whitespace-nowrap */}
        <span className="text-gray-900 font-normal">
          {data.lenderCreditPer} | {data.lenderCreditAmt}
        </span>

        {loanType && loanType.toLowerCase().includes("business") && (
          <>
            <span className="font-bold text-gray-700 whitespace-nowrap">YSP to Broker:</span>
            <span className="text-gray-900 font-normal">
              {data.yspToBrokerPer} | {data.yspToBrokerAmt}
            </span>
          </>
        )}
      </div>
    </div>
  );

  const getColorClass = (value) => {
    if (!value) return 'text-gray-900'; // Fallback for undefined/null
    const val = value.toLowerCase();
    if (val === 'pass') return 'text-green-600';
    if (val === 'fail' || val === 'error') return 'text-red-600';
    if (val === 'n/a') return 'text-gray-900';
    return 'text-gray-900'; // Default color for other cases
  };

  const renderComplianceReviewCard = (data) => (
    <div className="bg-white p-6 rounded-lg shadow-sm border text-sm">
      <div className="text-lg font-bold mb-4">Compliance Review Result</div>
      <div className="grid grid-cols-[minmax(120px,auto)_1fr] gap-y-2 mb-3">
        <span className="font-bold text-gray-700 whitespace-nowrap">High Cost Result:&nbsp;&nbsp;&nbsp;</span>
        <span className={`font-normal ${getColorClass(data.highCostResult)}`}>
          {data.highCostResult}
        </span>

        <span className="font-bold text-gray-700 whitespace-nowrap">Higher Priced Result:&nbsp;&nbsp;&nbsp;</span>
        <span className={`font-normal ${getColorClass(data.higherPricedResult)}`}>
          {data.higherPricedResult}
        </span>

        <span className="font-bold text-gray-700 whitespace-nowrap">State Rules Result:&nbsp;&nbsp;&nbsp;</span>
        <span className={`font-normal ${getColorClass(data.stateRulesResult)}`}>
          {data.stateRulesResult}
        </span>
      </div>
      {complianceReportAttachmentId && (
        <button
          className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm"
          onClick={downloadComplianceReport}
          title="Download Compliance Report"
        >
          <Download size={18} /> Download Compliance Report
        </button>
      )}
    </div>
  );


  const renderMilestoneContacts = () => {
    if (contacts.length > 0) {
      return (
        <div className="bg-white p-6 rounded-lg shadow-sm border text-sm">
          <div className="text-lg font-bold mb-4">Contacts</div>
          {contacts.map((c, idx) => (
            <div
              key={idx}
              className="mb-4 p-3 border rounded-lg bg-gray-50 shadow-sm"
            >
              <span className="font-bold text-gray-700 block mb-1">
                {c.role}
              </span>
              <div className="pl-3">
                <div className="text-gray-900 font-medium">{c.userName}</div>
                <div className="text-gray-600 text-sm">{c.email}</div>
                <div className="text-gray-600 text-sm">{c.phone}</div>
              </div>
            </div>
          ))}
        </div>
      );
    }
  };

  const renderBpIncomePitiCard = (data) => (
    <div className="bg-white p-6 rounded-lg shadow-sm border text-sm">
      <div className="text-lg font-bold mb-4">Income &amp; Taxes/Insurance</div>

      {data.showQualifyingIncome && (
        <div className="flex justify-between py-1">
          <span className="font-bold text-gray-700">Qualifying Income</span>
          <span className="text-gray-900">{data.qualifyingIncome || "$0.00"}</span>
        </div>
      )}

      <div className="flex justify-between py-1">
        <span className="font-bold text-gray-700">PITI</span>
        <span className="text-gray-900">{data.piti || "$0.00"}</span>
      </div>

      <div className="flex justify-between py-1">
        <span className="font-bold text-gray-700">Insurance</span>
        <span className="text-gray-900">{data.insurance || "$0.00"}</span>
      </div>

      <div className="flex justify-between py-1">
        <span className="font-bold text-gray-700">HOA Dues</span>
        <span className="text-gray-900">{data.hoaDue || "$0.00"}</span>
      </div>

      <div className="flex justify-between py-1">
        <span className="font-bold text-gray-700">Property Taxes</span>
        <span className="text-gray-900">{data.propertyTaxes || "$0.00"}</span>
      </div>
    </div>
  );

  const renderCard = (card) => {
    switch (card.type) {
      case 'loan-amount':
        return renderLoanAmountCard(card.data);
      case 'interest-rate':
        return renderInterestRateCard(card.data);
      case 'property':
        return renderPropertyCard(card.data);
      case 'loan-status':
        return renderLoanStatusCard(card.data);
      //case 'credit-info':
      //return renderCreditInfoCard(card.data);
      case 'key-dates':
        return renderKeyDatesCard(card.data);
      case 'compensation-factors':
        return renderCompensationTypeCard(card.data);
      case 'compliance-review':
        return renderComplianceReviewCard(card.data);
      case 'milestone-contacts':
        return renderMilestoneContacts(card.data);
      case 'bp-income-piti':
        return renderBpIncomePitiCard(card.data);
      default:
        return null;
    }
  };

  const redirectToLockComparisonPage = async () => {
    const tpoAppObject = await window.elli.script.getObject("tpoApplication");
    await tpoAppObject.pageRedirect("ExternalLoanPage", {
      id: loanID,
      extpageid: process.env.REACT_APP_LOCK_COMPARISON_PAGE_ID,
      pagename: "LockComparison"
    });
  };

  const downloadComplianceReport = async () => {
    try {
      const downloadInfo = await getComplianceReportDownloadUrl(accessToken, loanID, complianceReportAttachmentId);
      if (downloadInfo?.url) {
        await downloadComplianceReportPdf(downloadInfo.url, downloadInfo.authorizationHeader);
      }
    } catch (error) {
      console.error("Compliance Report download failed:", error);
    }
  };

  if (loading) {
    return <LoanSummaryFacebookLoader isPageCancelLoan={isPageCancelLoan} />;
  }

  return (
    <div className="min-h-screen p-6 summary-font">
      {!isPageCancelLoan ? (<div className="max-w-7xl mx-auto">
        {/* Milestones Section */}
        {renderMilestones()}

        {/* Loan Details Cards - Custom Layout */}
        <div className="grid_summary grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left Column (Stacked vertically) */}
          <div className="space-y-6">
            {/* Loan Amount */}
            {loanDetails.find(card => card.id === 1) &&
              renderCard(loanDetails.find(card => card.id === 1))}


            {/* Compensation Factor */}
            {!isCorrespondent && (
              loanDetails.find(card => card.id === 6) &&
              renderCard(loanDetails.find(card => card.id === 6))
            )}
            {/* Loan Status */}
            {loanDetails.find(card => card.id === 3) &&
              renderCard(loanDetails.find(card => card.id === 3))}

            {/* Income & Taxes/Insurance (below Compliance Review Result) */}
            {!isCorrespondent && (
              loanDetails.find(card => card.id === 9) &&
              renderCard(loanDetails.find(card => card.id === 9))
            )}

            {/* Compliance Review Result */}
            {!isCorrespondent && (
              loanDetails.find(card => card.id === 7) &&
              renderCard(loanDetails.find(card => card.id === 7))
            )}


          </div>

          {/* Middle Column (Stacked vertically) */}
          <div className="space-y-6">
            {/* Interest Rate */}
            {loanDetails.find(card => card.id === 2) &&
              renderCard(loanDetails.find(card => card.id === 2))}

            {/* Property */}
            {loanDetails.find(card => card.id === 4) &&
              renderCard(loanDetails.find(card => card.id === 4))}
            {/* Contact card */}
            {!isCorrespondent && (
              loanDetails.find(card => card.id === 8) &&
              renderCard(loanDetails.find(card => card.id === 8))
            )}
          </div>

          {/* Right Column - Key Dates (full height) */}
          {!isCorrespondent && (
            <div className="md:h-full">
              {loanDetails.find(card => card.id === 5) &&
                renderCard(loanDetails.find(card => card.id === 5))}
            </div>
          )}
          {/* Right Column - Key Dates (full height) */}
          {isCorrespondent && (
            <div className="md:h-full">
              {loanDetails.find(card => card.id === 8) &&
                renderCard(loanDetails.find(card => card.id === 8))}
            </div>
          )}
        </div>
        {tooltip.visible && (
          <div
            className="tooltip"
            style={{
              left: `${tooltip.x}px`,
              top: `${tooltip.y + 10}px`,
              opacity: 1,
              position: "absolute"
            }}
          >
            {tooltip.text}
          </div>
        )}
      </div>) : (<div className="max-w-7xl mx-auto">
        {/* Cancel Loan Header Section */}
        <CancelLoan />

        {/* Loan Details Cards - Custom Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column (Stacked vertically) */}
          <div className="space-y-6">
            {/* Loan Amount */}
            {loanDetails.find(card => card.id === 1) &&
              renderCard(loanDetails.find(card => card.id === 1))}

            {/* Compensation Factor */}
            {!isCorrespondent && (
              loanDetails.find(card => card.id === 6) &&
              renderCard(loanDetails.find(card => card.id === 6))
            )}

            {/* Loan Status */}
            {loanDetails.find(card => card.id === 3) &&
              renderCard(loanDetails.find(card => card.id === 3))}

            {/* Income & Taxes/Insurance (below Compliance Review Result) */}
            {!isCorrespondent && (
              loanDetails.find(card => card.id === 9) &&
              renderCard(loanDetails.find(card => card.id === 9))
            )}

            {/* Compliance Review Result */}
            {!isCorrespondent && (
              loanDetails.find(card => card.id === 7) &&
              renderCard(loanDetails.find(card => card.id === 7))
            )}
          </div>

          {/* Middle Column (Stacked vertically) */}
          <div className="space-y-6">
            {/* Interest Rate */}
            {loanDetails.find(card => card.id === 2) &&
              renderCard(loanDetails.find(card => card.id === 2))}

            {/* Property */}
            {loanDetails.find(card => card.id === 4) &&
              renderCard(loanDetails.find(card => card.id === 4))}
          </div>
        </div>

        {tooltip.visible && (
          <div
            className="tooltip"
            style={{
              left: `${tooltip.x}px`,
              top: `${tooltip.y + 10}px`,
              opacity: 1,
              position: "absolute"
            }}
          >
            {tooltip.text}
          </div>
        )}
      </div>)}
    </div>
  );
};
export default Index;